import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import { Person, Edit, Save, Logout } from "@mui/icons-material";

const ProfileInformationCard = ({ user, onUpdate, onLogout }) => {
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: user });

  useEffect(() => {
    if (user) reset(user);
  }, [user, reset]);

  const onSubmit = async (data) => {
    try {
      await onUpdate(data);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2.5,
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Person sx={{ color: "primary.main" }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Profile Information
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Avatar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
        <Avatar
          sx={{
            width: 72,
            height: 72,
            bgcolor: "primary.main",
            fontSize: "1.75rem",
          }}
        >
          {user?.firstName?.[0]}
          {user?.lastName?.[0]}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Chip
            label={user?.status}
            color={user?.status === "active" ? "success" : "default"}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>
      </Box>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="First Name"
              size="small"
              disabled={!isEditing}
              {...register("firstName", {
                required: "First name is required",
                minLength: { value: 2, message: "Min 2 characters" },
              })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Last Name"
              size="small"
              disabled={!isEditing}
              {...register("lastName", {
                required: "Last name is required",
                minLength: { value: 2, message: "Min 2 characters" },
              })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              size="small"
              disabled={!isEditing}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Invalid email address",
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">
              Member Since
            </Typography>
            <Typography variant="body2">
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString()
                : "—"}
            </Typography>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<Logout />}
            onClick={onLogout}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Logout
          </Button>
          {isEditing ? (
            <Button
              variant="contained"
              startIcon={<Save />}
              type="submit"
              disabled={!isDirty}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Save Changes
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Edit Profile
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default ProfileInformationCard;
